import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CmsConfig } from '../../config/cms.config';
import { Address, Title } from '@kion/data-models';
import { BehaviorSubject, Observable } from 'rxjs';
import { UsersFacade } from '@kion/data-access/data-access-users';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { TitlesFacade } from '@kion/data-access/data-access-titles';
import { map } from 'rxjs/operators';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Checkbox } from '../../../../../../shared/ui/src/lib/shared/elements/checkbox/checkbox.component';
import { ToastService } from '@kion/ui';
import { TranslateService } from '@ngx-translate/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { AuthsFacade } from '@kion/data-access/data-access-auths';
import { BasketsFacade } from '@kion/data-access/data-access-baskets';
import { DOCUMENT } from '@angular/common';
import { SubSink } from 'subsink';
import { MsalService } from '@kion/core';

@Component({
  selector: 'kion-cms-sign-up-complete',
  templateUrl: './sign-up-complete.component.html',
  styleUrls: ['./sign-up-complete.component.scss']
})
export class SignUpCompleteComponent implements OnInit, OnDestroy {

  checkboxes: Checkbox[];

  isoCode: string;
  address$: BehaviorSubject<Partial<Address>> = new BehaviorSubject<Partial<Address>>(null);

  firstName: string;
  lastName: string;
  email: string;

  titles$: Observable<{key: string, value: string}[]>;
  checkboxesSet$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private window: Window;

  private readonly subSink: SubSink;

  constructor(
    private cmsConfig: CmsConfig,
    private usersFacade: UsersFacade,
    private observer: CorrelationObserverService,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private router: Router,
    private titlesFacade: TitlesFacade,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authsFacade: AuthsFacade,
    private basketsFacade: BasketsFacade,
    private msalService: MsalService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.subSink = new SubSink();
    this.isoCode = this.cmsConfig.isoCode;
    this.window = this.document.defaultView;
  }

  ngOnInit(): void {
    this.titlesFacade.loadAll();
    const account = this.msalService.getAccount();
    this.firstName = (account as any).given_name;
    this.lastName = (account as any).family_name;
    this.email = (account as any).email || (account as any).emails[0];
    this.titles$ = this.titlesFacade.all$.pipe(
      map(
        (titles: Title[]) => titles.map(
          (title: Title) => {
            return {
              key: title.code,
              value: title.name
            }
          }
        )
      )
    );
    this.checkboxes = [
      {
        title: this.translateService.instant(
          "signUp.checkbox.privacy.description",
          {
            link: '<a href="'+ this.cmsConfig.routes?.privacyPolicyBaseRoute +'" target="_blank"> ' +
              this.translateService.instant("signUp.checkbox.privacy.link")  +
              ' </a>'
            }
          ),
        isLink: false,
        required: true,
        checked: false,
        titleAsHtml: true
      },
      {
        title: this.translateService.instant(
          "signUp.checkbox.terms.description",
          {
            link: '<a href="'+ this.cmsConfig.routes?.termsBaseRoute +'" target="_blank"> ' +
              this.translateService.instant("signUp.checkbox.terms.link")  +
              ' </a>'
          }
        ),
        isLink: false,
        required: true,
        checked: false,
        titleAsHtml: true
      }
    ];
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  addressChanged(address: Partial<Address>): void {
    this.address$.next(address);
  }
  register(): void {
    const address = this.address$.value;
    const account = this.msalService.getAccount();
    this.subSink.sink = this.observer.subscribeToAction(
      this.usersFacade.registerUser(
        this.msalService.getRawIdToken(),
        this.msalService.getRawIdToken(),
        address.firstName,
        address.lastName,
        address.email,
        account.tfp || account.acr,
        account.oid || account.sub,
        null,
        address
      ),
      () => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.authsFacade.userAuth(
            address.email,
            this.msalService.getRawIdToken(),
            this.msalService.getRawIdToken(),
            account.tfp || account.acr,
            account.oid || account.sub,
          ),
          () => {
            this.subSink.sink = this.observer.subscribeToAction(
              this.basketsFacade.transformAnonymousToPersonal('anonymous', this.window.localStorage.getItem('basketId')),
              () => {
                this.redirectUserAfterRegister();
              },
              () => {
                this.redirectUserAfterRegister();
              }
            )
          },
          () => {
            this.toastService.show(
              {
                type: 'error',
                text: 'login failed please try manually'
              }
            );
          }
        )
      },
      () => {
        this.toastService.show(
          {
            type: 'error',
            text: 'register failed'
          }
        );
      }
    );
  }

  private redirectUserAfterRegister(): void {
    const isCheckout = this.route.snapshot.data.checkout;
    if (isCheckout) {
      this.router.navigate([this.cmsConfig.routes?.checkoutBaseRoute, this.cmsConfig.routes?.checkoutRoutes?.address]).then(() => { });
      return;
    }
    this.router.navigate(['']).then(() => { });
  }

  cancel(): void {
    this.router.navigate(['sign-out']).then(() => { });
  }

  requiredCheckboxesSet(checked: boolean) {
    this.checkboxesSet$.next(checked)
  }
}
